import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import api from "../services/api";
import ImportExportBar from "./ImportExportBar";
import Pagination from "./Pagination";

const PAGE_SIZE = 20;

const UOM_OPTIONS = [
  "Nos", "Pcs", "Kg", "Gram", "Litre", "ML", "Dozen", "Pack", "Bag", "Bottle",
  "Box", "Case", "Carton", "CTN", "Set", "Pair", "Roll", "Sheet",
];

const withCurrentOption = (options, current) =>
  current && !options.includes(current) ? [current, ...options] : options;

const emptyForm = {
  brandId: "",
  skuCode: "",
  name: "",
  uom: "",
  category: "",
  hsnCode: "",
  packSize: "1",
  mrp1: "",
  mrp2: "",
  boxUom: "",
  unitsPerBox: "",
  caseUom: "",
  unitsPerCase: "",
  gstApplicable: true,
  typeOfSupply: "GOODS",
  cgstPercent: "",
  sgstPercent: "",
  distributorMarginPercent: "",
  retailerMarginPercent: "",
  additionalMarginPercent: "",
  weight: "",
  billByUom: "",
  purchaseTaxPercent: "",
  salesTaxPercent: "",
  alternateUom: "",
  alternateUomSize: "",
  minStockLevel: "",
  maxStockLevel: "",
  // price of one base unit before tax; the unit price a new sales invoice line starts with
  baseUnitPrice: "",
  // kept on the product's price list (newest entry), saved separately
  sellingPrice: "",
  purchasePrice: "",
  active: true,
};

const numOrNull = (value) => (value === "" || value === null || value === undefined ? null : Number(value));
const fmtPrice = (value) =>
  value === null || value === undefined ? "-" : Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const Products = () => {
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [nameFilter, setNameFilter] = useState("");
  const [skuFilter, setSkuFilter] = useState("");
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  // selling / purchase price of each listed product (newest price list entry)
  const [prices, setPrices] = useState({});

  const brandNameById = useMemo(() => {
    const map = new Map();
    brands.forEach((brand) => map.set(brand.id, brand.name));
    return map;
  }, [brands]);

  const categoryDescriptionByCode = useMemo(() => {
    const map = new Map();
    categories.forEach((c) => map.set(c.code, c.description));
    return map;
  }, [categories]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [productsData, brandsData, categoriesData] = await Promise.all([
        api.get("/api/v1/products", { page, size: PAGE_SIZE, name: nameFilter.trim() || undefined, skuCode: skuFilter.trim() || undefined }),
        api.get("/api/v1/brands", { size: 200 }),
        api.get("/api/v1/product-categories", { size: 200 }),
      ]);
      const list = productsData?.content || [];
      setProducts(list);
      if (list.length) {
        api
          .get("/api/v1/products/prices", { productIds: list.map((p) => p.id).join(",") })
          .then((rows) => setPrices(Object.fromEntries((rows || []).map((row) => [row.productId, row]))))
          .catch(() => setPrices({}));
      } else setPrices({});
      setTotalPages(productsData?.totalPages ?? 0);
      setTotalElements(productsData?.totalElements ?? 0);
      setBrands(brandsData?.content || []);
      setCategories(categoriesData?.content || []);
    } catch (error) {
      toast.error(error.message || "Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  // Changing a filter should always jump back to page 0 — otherwise a narrower result set can land you past
  // its last page, silently showing nothing.
  useEffect(() => {
    setPage(0);
  }, [nameFilter, skuFilter]);

  useEffect(() => {
    // Debounce the free-text filters so we don't fire a request on every keystroke.
    const timeout = setTimeout(loadData, nameFilter || skuFilter ? 400 : 0);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, nameFilter, skuFilter]);

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEdit = (product) => {
    setEditingId(product.id);
    setFormData({
      brandId: product.brandId ?? "",
      skuCode: product.skuCode || "",
      name: product.name || "",
      uom: product.uom || "",
      category: product.category || "",
      hsnCode: product.hsnCode || "",
      packSize: product.packSize ?? "1",
      mrp1: product.mrp1 ?? "",
      mrp2: product.mrp2 ?? "",
      boxUom: product.boxUom || "",
      unitsPerBox: product.unitsPerBox ?? "",
      caseUom: product.caseUom || "",
      unitsPerCase: product.unitsPerCase ?? "",
      gstApplicable: product.gstApplicable ?? true,
      typeOfSupply: product.typeOfSupply || "GOODS",
      cgstPercent: product.cgstPercent ?? "",
      sgstPercent: product.sgstPercent ?? "",
      distributorMarginPercent: product.distributorMarginPercent ?? "",
      retailerMarginPercent: product.retailerMarginPercent ?? "",
      additionalMarginPercent: product.additionalMarginPercent ?? "",
      weight: product.weight ?? "",
      purchaseTaxPercent: product.purchaseTaxPercent ?? "",
      salesTaxPercent: product.salesTaxPercent ?? "",
      billByUom: product.billByUom || "",
      alternateUom: product.alternateUom || "",
      alternateUomSize: product.alternateUomSize ?? "",
      minStockLevel: product.minStockLevel ?? "",
      maxStockLevel: product.maxStockLevel ?? "",
      baseUnitPrice: product.baseUnitPrice ?? "",
      sellingPrice: prices[product.id]?.sellingPrice ?? "",
      purchasePrice: prices[product.id]?.purchasePrice ?? "",
      active: product.active ?? true,
    });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleBoxToggle = (e) => {
    const enabled = e.target.checked;
    setFormData((prev) => ({ ...prev, boxUom: enabled ? "Box" : "", unitsPerBox: enabled ? prev.unitsPerBox : "" }));
  };

  const handleCaseToggle = (e) => {
    const enabled = e.target.checked;
    setFormData((prev) => ({ ...prev, caseUom: enabled ? "Case" : "", unitsPerCase: enabled ? prev.unitsPerCase : "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.brandId) {
      toast.error("Please select a brand");
      return;
    }
    setSaving(true);
    try {
      const { sellingPrice, purchasePrice, ...productFields } = formData;
      const payload = {
        ...productFields,
        brandId: Number(formData.brandId),
        packSize: formData.packSize === "" ? 1 : Number(formData.packSize),
        mrp1: formData.mrp1 === "" ? null : Number(formData.mrp1),
        mrp2: formData.mrp2 === "" ? null : Number(formData.mrp2),
        boxUom: formData.boxUom || null,
        unitsPerBox: formData.unitsPerBox === "" ? null : Number(formData.unitsPerBox),
        caseUom: formData.caseUom || null,
        unitsPerCase: formData.unitsPerCase === "" ? null : Number(formData.unitsPerCase),
        cgstPercent: formData.cgstPercent === "" ? null : Number(formData.cgstPercent),
        sgstPercent: formData.sgstPercent === "" ? null : Number(formData.sgstPercent),
        distributorMarginPercent: formData.distributorMarginPercent === "" ? null : Number(formData.distributorMarginPercent),
        retailerMarginPercent: formData.retailerMarginPercent === "" ? null : Number(formData.retailerMarginPercent),
        additionalMarginPercent: formData.additionalMarginPercent === "" ? null : Number(formData.additionalMarginPercent),
        weight: formData.weight === "" ? null : Number(formData.weight),
        purchaseTaxPercent: formData.purchaseTaxPercent === "" ? null : Number(formData.purchaseTaxPercent),
        salesTaxPercent: formData.salesTaxPercent === "" ? null : Number(formData.salesTaxPercent),
        billByUom: formData.billByUom || null,
        alternateUom: formData.alternateUom.trim() || null,
        alternateUomSize: formData.alternateUom.trim() ? numOrNull(formData.alternateUomSize) : null,
        minStockLevel: numOrNull(formData.minStockLevel),
        maxStockLevel: numOrNull(formData.maxStockLevel),
        baseUnitPrice: numOrNull(formData.baseUnitPrice),
      };
      const saved = editingId
        ? await api.put(`/api/v1/products/${editingId}`, payload)
        : await api.post("/api/v1/products", payload);
      // selling / purchase price live on the price list; update them when entered or changed
      const before = editingId ? prices[editingId] : null;
      const priceChanged =
        (sellingPrice !== "" && Number(sellingPrice) !== Number(before?.sellingPrice ?? NaN)) ||
        (purchasePrice !== "" && Number(purchasePrice) !== Number(before?.purchasePrice ?? NaN));
      if (priceChanged && saved?.id) {
        await api.put(`/api/v1/products/${saved.id}/prices`, { sellingPrice: numOrNull(sellingPrice), purchasePrice: numOrNull(purchasePrice) });
      }
      toast.success(editingId ? "Product updated" : "Product created");
      setShowModal(false);
      loadData();
    } catch (error) {
      toast.error(error.message || "Failed to save product");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4" style={{ position: "relative" }}>
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading products...</div>
          </div>
        </div>
      ) : null}

      <div className="filters-bar">
        <input
          type="text"
          placeholder="Filter by name"
          value={nameFilter}
          onChange={(e) => setNameFilter(e.target.value)}
          style={{ width: "160px" }}
        />
        <input
          type="text"
          placeholder="Filter by SKU"
          value={skuFilter}
          onChange={(e) => setSkuFilter(e.target.value)}
          style={{ width: "140px" }}
        />
        {nameFilter || skuFilter ? (
          <button
            type="button"
            className="secondary"
            onClick={() => {
              setNameFilter("");
              setSkuFilter("");
            }}
          >
            Clear filters
          </button>
        ) : null}
        <button className="primary" onClick={openCreate}>
          + New Product
        </button>
        <ImportExportBar
          importPath="/api/v1/products/import"
          exportPath="/api/v1/products/export"
          exportFilename="products.xlsx"
          label="Products"
          onImported={loadData}
        />
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>SKU</th>
              <th>Name</th>
              <th>Brand</th>
              <th>UOM</th>
              <th>Category</th>
              <th>HSN</th>
              <th>Pack Size</th>
              <th>MRP1</th>
              <th>MRP2</th>
              <th>GST</th>
              <th>Supply Type</th>
              <th>CGST %</th>
              <th>SGST %</th>
              <th title="Distributor / Retailer / Additional margin %">Margin % (D / R / A)</th>
              <th title="Purchase / Sales tax %">Tax % (P / S)</th>
              <th>Weight</th>
              <th>Bill By</th>
              <th>Selling Price</th>
              <th>Purchase Price</th>
              <th title="Alternate unit and its size in base units">Alt UOM</th>
              <th title="Minimum / maximum stock level">Stock Level (Min / Max)</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.length === 0 ? (
              <tr>
                <td colSpan={24}>No products found.</td>
              </tr>
            ) : (
              products.map((product) => (
                <tr key={product.id}>
                  <td>{product.id}</td>
                  <td>{product.skuCode}</td>
                  <td>{product.name}</td>
                  <td>{brandNameById.get(product.brandId) || product.brandId}</td>
                  <td>{product.uom}</td>
                  <td>
                    {product.category
                      ? categoryDescriptionByCode.has(product.category)
                        ? `${product.category} (${categoryDescriptionByCode.get(product.category)})`
                        : product.category
                      : "-"}
                  </td>
                  <td>{product.hsnCode || "-"}</td>
                  <td>{product.packSize}</td>
                  <td>{product.mrp1 ?? "-"}</td>
                  <td>{product.mrp2 ?? "-"}</td>
                  <td>
                    <span className={`badge ${product.gstApplicable ? "badge-green" : "badge-gray"}`}>
                      {product.gstApplicable ? "Yes" : "No"}
                    </span>
                  </td>
                  <td>{product.typeOfSupply === "SERVICES" ? "Services" : "Goods"}</td>
                  <td>{product.cgstPercent ?? "-"}</td>
                  <td>{product.sgstPercent ?? "-"}</td>
                  <td>
                    {[product.distributorMarginPercent, product.retailerMarginPercent, product.additionalMarginPercent]
                      .map((value) => value ?? "-")
                      .join(" / ")}
                  </td>
                  <td>{[product.purchaseTaxPercent, product.salesTaxPercent].map((value) => value ?? "-").join(" / ")}</td>
                  <td>{product.weight ?? "-"}</td>
                  <td>{product.billByUom || product.uom || "-"}</td>
                  <td>{fmtPrice(prices[product.id]?.sellingPrice)}</td>
                  <td>{fmtPrice(prices[product.id]?.purchasePrice)}</td>
                  <td>{product.alternateUom ? `${product.alternateUom} (${Number(product.alternateUomSize || 0)} ${product.uom})` : "-"}</td>
                  <td>{[product.minStockLevel, product.maxStockLevel].map((v) => (v === null || v === undefined ? "-" : Number(v))).join(" / ")}</td>
                  <td>
                    <span className={`badge ${product.active ? "badge-green" : "badge-gray"}`}>
                      {product.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="action-buttons">
                    <button className="secondary" onClick={() => openEdit(product)}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} totalElements={totalElements} onPageChange={setPage} />

      {showModal ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-box">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">{editingId ? "Edit Product" : "Create Product"}</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                Close
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Brand</label>
                  <select name="brandId" value={formData.brandId} onChange={handleChange} required>
                    <option value="">Select brand</option>
                    {brands.map((brand) => (
                      <option key={brand.id} value={brand.id}>
                        {brand.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>SKU Code</label>
                  <input name="skuCode" value={formData.skuCode} onChange={handleChange} required />
                </div>
                <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                  <label>Name</label>
                  <input name="name" value={formData.name} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>UOM</label>
                  <select name="uom" value={formData.uom} onChange={handleChange} required>
                    <option value="">Select UOM</option>
                    {withCurrentOption(UOM_OPTIONS, formData.uom).map((u) => (
                      <option key={u} value={u}>
                        {u}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <select name="category" value={formData.category} onChange={handleChange}>
                    <option value="">Select category</option>
                    {/* a category typed in before this became a dropdown stays selectable until it is changed */}
                    {formData.category && !categories.some((c) => c.code === formData.category) ? (
                      <option value={formData.category}>{formData.category}</option>
                    ) : null}
                    {categories.map((c) => (
                      <option key={c.id} value={c.code}>
                        {c.description ? `${c.code} (${c.description})` : c.code}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>HSN Code</label>
                  <input name="hsnCode" value={formData.hsnCode} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Pack Size</label>
                  <input type="number" min="0" step="0.01" name="packSize" value={formData.packSize} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>MRP1</label>
                  <input type="number" min="0" step="0.01" name="mrp1" value={formData.mrp1} onChange={handleChange} placeholder="e.g. 42.00" />
                </div>
                <div className="form-group">
                  <label>MRP2</label>
                  <input type="number" min="0" step="0.01" name="mrp2" value={formData.mrp2} onChange={handleChange} placeholder="e.g. 45.00" />
                </div>
                <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                  <label style={{ marginBottom: "4px", display: "block" }}>Packaging Unit Hierarchy (optional)</label>
                  <div className="page-subtitle" style={{ marginBottom: "8px" }}>
                    UOM above is the base unit stock is always tracked in (e.g. "Nos"). Turn these on if this
                    product is also sold/purchased in bigger packs — e.g. Box = 6 Nos, Case = 12 Boxes.
                  </div>
                </div>
                <div className="form-group">
                  <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                    <input type="checkbox" checked={!!formData.boxUom} onChange={handleBoxToggle} />
                    Sold in Boxes
                  </label>
                </div>
                {formData.boxUom ? (
                  <div className="form-group">
                    <label>Units Per Box (in base UOM)</label>
                    <input type="number" min="0" step="0.001" name="unitsPerBox" value={formData.unitsPerBox} onChange={handleChange} placeholder="e.g. 6" autoFocus />
                  </div>
                ) : null}
                <div className="form-group">
                  <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                    <input type="checkbox" checked={!!formData.caseUom} onChange={handleCaseToggle} />
                    Sold in Cases
                  </label>
                </div>
                {formData.caseUom ? (
                  <div className="form-group">
                    <label>Units Per Case (in Boxes)</label>
                    <input type="number" min="0" step="0.001" name="unitsPerCase" value={formData.unitsPerCase} onChange={handleChange} placeholder="e.g. 12" autoFocus />
                  </div>
                ) : null}
                <div className="form-group">
                  <label>GST Applicable</label>
                  <select
                    name="gstApplicable"
                    value={formData.gstApplicable ? "true" : "false"}
                    onChange={(e) => setFormData((prev) => ({ ...prev, gstApplicable: e.target.value === "true" }))}
                  >
                    <option value="true">Yes — taxable</option>
                    <option value="false">No — exempt</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Type of Supply</label>
                  <select name="typeOfSupply" value={formData.typeOfSupply} onChange={handleChange}>
                    <option value="GOODS">Goods</option>
                    <option value="SERVICES">Services</option>
                  </select>
                  <label>CGST %</label>
                  <input type="number" min="0" step="0.001" name="cgstPercent" value={formData.cgstPercent} onChange={handleChange} placeholder="e.g. 2.5" />
                </div>
                <div className="form-group">
                  <label>SGST %</label>
                  <input type="number" min="0" step="0.001" name="sgstPercent" value={formData.sgstPercent} onChange={handleChange} placeholder="e.g. 2.5" />
                </div>
                <div className="form-group">
                  <label>Purchase Tax %</label>
                  <input type="number" min="0" max="100" step="0.001" name="purchaseTaxPercent" value={formData.purchaseTaxPercent} onChange={handleChange} placeholder="e.g. 18" />
                </div>
                <div className="form-group">
                  <label>Sales Tax %</label>
                  <input type="number" min="0" max="100" step="0.001" name="salesTaxPercent" value={formData.salesTaxPercent} onChange={handleChange} placeholder="e.g. 18" />
                </div>
                <div className="form-group">
                  <label>Distributor Margin %</label>
                  <input type="number" min="0" max="100" step="0.001" name="distributorMarginPercent" value={formData.distributorMarginPercent} onChange={handleChange} placeholder="e.g. 6" />
                </div>
                <div className="form-group">
                  <label>Retailer Margin %</label>
                  <input type="number" min="0" max="100" step="0.001" name="retailerMarginPercent" value={formData.retailerMarginPercent} onChange={handleChange} placeholder="e.g. 10" />
                </div>
                <div className="form-group">
                  <label>Additional Margin %</label>
                  <input type="number" min="0" max="100" step="0.001" name="additionalMarginPercent" value={formData.additionalMarginPercent} onChange={handleChange} placeholder="e.g. 1.5" />
                </div>
                <div className="form-group">
                  <label>Weight (kg, per {formData.uom || "unit"})</label>
                  <input type="number" min="0" step="0.001" name="weight" value={formData.weight} onChange={handleChange} placeholder="e.g. 0.25" />
                </div>
                <div className="form-group">
                  <label>Bill By (unit the product is billed in)</label>
                  <select name="billByUom" value={formData.billByUom} onChange={handleChange}>
                    <option value="">Same as UOM{formData.uom ? ` (${formData.uom})` : ""}</option>
                    {withCurrentOption(UOM_OPTIONS, formData.billByUom).map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Base Unit Price (per {formData.uom || "unit"}, before tax)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    name="baseUnitPrice"
                    value={formData.baseUnitPrice}
                    onChange={handleChange}
                    placeholder="Unit price on new invoices"
                    title="Picked as the unit price when a sales invoice is created on the admin panel or the mobile app"
                  />
                </div>
                <div className="form-group">
                  <label>Selling Price (per {formData.uom || "unit"}, before tax)</label>
                  <input type="number" min="0" step="0.01" name="sellingPrice" value={formData.sellingPrice} onChange={handleChange} placeholder="e.g. 38.50" />
                </div>
                <div className="form-group">
                  <label>Purchase Price (per {formData.uom || "unit"}, before tax)</label>
                  <input type="number" min="0" step="0.01" name="purchasePrice" value={formData.purchasePrice} onChange={handleChange} placeholder="e.g. 34.00" />
                </div>
                <div className="form-group">
                  <label>Alternate UOM</label>
                  <input name="alternateUom" value={formData.alternateUom} onChange={handleChange} maxLength={50} placeholder="e.g. Strip, Outer, Dozen" />
                </div>
                <div className="form-group">
                  <label>Alternate UOM Size (in {formData.uom || "base units"})</label>
                  <input type="number" min="0" step="0.001" name="alternateUomSize" value={formData.alternateUomSize} onChange={handleChange}
                    placeholder="e.g. 12" disabled={!formData.alternateUom.trim()} required={!!formData.alternateUom.trim()} />
                </div>
                <div className="form-group">
                  <label>Minimum Stock Level</label>
                  <input type="number" min="0" step="0.001" name="minStockLevel" value={formData.minStockLevel} onChange={handleChange} placeholder="Low stock below this" />
                </div>
                <div className="form-group">
                  <label>Maximum Stock Level</label>
                  <input type="number" min="0" step="0.001" name="maxStockLevel" value={formData.maxStockLevel} onChange={handleChange} placeholder="Excess stock above this" />
                </div>
                <div className="form-group">
                  <label>Active</label>
                  <select
                    name="active"
                    value={formData.active ? "true" : "false"}
                    onChange={(e) => setFormData((prev) => ({ ...prev, active: e.target.value === "true" }))}
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="primary" disabled={saving}>
                  {saving ? "Saving..." : editingId ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Products;
